import {
	Button,
	formControlLabelClasses,
	InputAdornment,
	Stack,
	Typography,
} from "@mui/material";
import { debounce } from "@mui/material/utils";
import { CoHostType } from "@onair/constants";
import { Field } from "@onair/minimal-ui/hook-form";
import { Iconify } from "@onair/minimal-ui/iconify";
import { getUsersBySearch } from "@onair/services/user";
import { useAuthStore } from "@onair/store/auth";
import Avatar from "@onair/ui/avatar";
import { ExtraImageSvg } from "@onair/ui/svg/extra-image";
import dayjs from "@onair/utils/dayjs";
import React, { useEffect, useState } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import InputLabelWizard from "../input-label-wizard.tsx";
import { WizardSchemaType } from "../index";

const SortableEventSessionForm = ({
	index,
	methods,
}: {
	index: number;
	methods: UseFormReturn<WizardSchemaType>;
}) => {
	const [isSearching, setIsSearching] = useState(false);
	const [userOptions, setUserOptions] = useState([]);

	const currentUser = useAuthStore((state) => state);

	const {
		control,
		setValue,
		formState: { errors },
	} = methods;

	const coHostType = useWatch({
		name: `stepTimeline.sessions.${index}.coHostType`,
		control,
	});

	const startDate = useWatch({
		name: `stepTimeline.sessions.${index}.startDate`,
		control,
	});

	const coHostSearchText = useWatch({
		name: `stepTimeline.sessions.${index}._coHostSearch`,
		control,
	});

	const coHostEntity = useWatch({
		name: `stepTimeline.sessions.${index}.coHostEntity`,
		control,
	});

	const fetch = React.useMemo(
		() =>
			debounce(
				async (
					request: { input: string },
					callback: (results?: any[]) => void,
				) => {
					const { items } = await getUsersBySearch(request.input);

					callback(items || []);
					setIsSearching(false);
				},
				400,
			),
		[currentUser],
	);

	useEffect(() => {
		let active = true;

		if (!coHostSearchText) {
			setUserOptions([]);
			return undefined;
		}

		fetch({ input: coHostSearchText }, (results?: any[]) => {
			if (active) {
				let newOptions: any[] = [];

				if (results) {
					newOptions = [...newOptions, ...results];
				}

				// @ts-ignore
				setUserOptions(newOptions);
			}
		});

		return () => {
			active = false;
		};
	}, [coHostSearchText, fetch]);

	return (
		<Stack spacing={3}>
			<Stack spacing={1}>
				<Field.Text
					name={`stepTimeline.sessions.${index}.name`}
					placeholder={`VD: Buổi ${index + 1} - Ứng dụng AI vào công việc`}
					label="Tên sự kiện"
					required
				/>
				<Typography variant="caption" color="text.secondary" align="right">
					Tối đa <strong>100</strong> ký tự.
				</Typography>
			</Stack>
			<Stack
				sx={{
					flexDirection: {
						xs: "column",
						md: "row",
					},
				}}
				spacing={2}
			>
				<Field.DateTimePicker
					name={`stepTimeline.sessions.${index}.startDate`}
					label="Thời gian bắt đầu"
					slotProps={{
						textField: {
							required: true,
						},
					}}
					disablePast
				/>
				<Field.DateTimePicker
					name={`stepTimeline.sessions.${index}.endDate`}
					label="Thời gian kết thúc"
					slotProps={{
						textField: {
							required: true,
						},
					}}
					disablePast
					minDateTime={dayjs(startDate).add(15, "minute")}
				/>
			</Stack>
			<Stack spacing={1}>
				<InputLabelWizard>Nội dung</InputLabelWizard>
				<Field.Editor
					placeholder=""
					name={`stepTimeline.sessions.${index}.content`}
					sx={{
						["& .nml__editor__content__root"]: {
							py: 1.5,
						},
					}}
				/>
			</Stack>
			<Stack spacing={0.5}>
				<Stack spacing={1}>
					<InputLabelWizard>Ảnh sự kiện</InputLabelWizard>
					<Stack direction="row" alignItems="center">
						<Typography variant="caption" color="text.secondary">
							Kích thước chuẩn: <strong>1280 x 720 px</strong> (16:9)
						</Typography>

						<Typography variant="caption" color="text.secondary" sx={{ mx: 1 }}>
							&#x2022;
						</Typography>

						<Typography variant="caption" color="text.secondary">
							File <strong>.jpg, .png</strong>
						</Typography>
					</Stack>
				</Stack>

				<Field.Upload
					name={`stepTimeline.sessions.${index}.banner`}
					maxSize={3145728}
					accept={{
						"image/jpeg": [],
						"image/jpg": [],
						"image/png": [],
					}}
					sx={{
						width: {
							xs: "80%",
							md: "50%",
						},
						mt: 0.5,
					}}
					placeholder={
						<Stack justifyContent="center" alignItems="center">
							<ExtraImageSvg />
							<Button
								startIcon={<Iconify icon="eva:cloud-upload-fill" />}
								variant="contained"
								color="primary"
							>
								Tải ảnh
							</Button>
						</Stack>
					}
					onDelete={() =>
						setValue(`stepTimeline.sessions.${index}.banner`, null, {
							shouldValidate: true,
						})
					}
				/>
			</Stack>
			<Stack display="none" spacing={1}>
				<InputLabelWizard>Thêm Co-host</InputLabelWizard>
				<Field.RadioGroup
					name={`stepTimeline.sessions.${index}.coHostType`}
					options={[
						{
							label: "Tài khoản trên OnAir",
							value: CoHostType.SYSTEM,
						},
						{ label: "Email", value: CoHostType.EMAIL },
					]}
					sx={{
						gap: 2,
						flexDirection: "row",
						[`& .${formControlLabelClasses.root}`]: {
							width: "50%",
							mr: 0,
						},
					}}
				/>
				{coHostType === CoHostType.SYSTEM ? (
					<Field.Autocomplete
						name={`stepTimeline.sessions.${index}.coHostEntity`}
						label="Tài khoản hoặc Email"
						options={userOptions}
						onChange={(_, value) => {
							setValue(`stepTimeline.sessions.${index}.coHostEntity`, value);
							setValue(`stepTimeline.sessions.${index}._coHostSearch`, "");
						}}
						fullWidth
						// @ts-ignore
						InputProps={{
							startAdornment: coHostEntity ? (
								<InputAdornment sx={{ ml: 0.5 }} position="start">
									<Avatar
										size={24}
										alt={coHostEntity?.fullName}
										src={coHostEntity?.avatar}
									/>
								</InputAdornment>
							) : null,
						}}
						disableClearable
						noOptionsText={
							coHostSearchText
								? isSearching
									? "Đang tìm kiếm..."
									: "Không tìm thấy tài khoản phù hợp."
								: "Tìm kiếm tài khoản bằng tên hoặc email"
						}
						filterOptions={(x) => x}
						getOptionLabel={(option) =>
							typeof option === "string" ? option : option.fullName
						}
						autoComplete
						onInputChange={(_, value) => {
							setIsSearching(true);
							setValue(`stepTimeline.sessions.${index}._coHostSearch`, value);
							if (!value) {
								setValue(`stepTimeline.sessions.${index}.coHostEntity`, null);
							}
						}}
						renderOption={(props, option) => {
							const { key, ...optionProps } = props;
							const shouldDisable = option.id === currentUser?.id;

							if (shouldDisable) {
								optionProps.onClick = () => { };
							}

							return (
								<li key={key} {...optionProps}>
									<Stack
										className={shouldDisable ? "opacity-60" : ""}
										direction="row"
										spacing={2}
									>
										<Avatar
											size={40}
											alt={option.fullName}
											src={option.avatar}
										/>
										<Stack>
											<Typography variant="body2">{option.fullName}</Typography>
											<Typography variant="body2" color="text.secondary">
												{option.email}
											</Typography>
										</Stack>
									</Stack>
								</li>
							);
						}}
					/>
				) : (
					<Stack>
						<Stack
							direction={{
								xs: "column",
								md: "row",
							}}
							spacing={2}
						>
							<Field.Text
								name={`stepTimeline.sessions.${index}.coHost.email`}
								label="Email"
								required
							/>
							<Field.Text
								name={`stepTimeline.sessions.${index}.coHost.name`}
								label="Tên Co-host"
								required
							/>
						</Stack>
					</Stack>
				)}
			</Stack>
		</Stack>
	);
};

export default SortableEventSessionForm;
